import React, {
  memo,
  forwardRef,
  useImperativeHandle,
  useRef,
  RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Actions,
  Getters,
  Plugin,
  Template,
  TemplateConnector,
  TemplatePlaceholder,
} from '@devexpress/dx-react-core';
import TableSelectionPanel from './TableSelectionPanel';
import { RowSelectionConfig } from '../../../../../services/Main/types.Component';
import { TableSelectionRef } from './types';

export interface TableSelectionPanelPluginProps {
  rowSelectionConfig: RowSelectionConfig;
  tableSelectionPanelRef?: RefObject<HTMLDivElement>;
}

const TableSelectionPanelPlugin = forwardRef<
  TableSelectionRef,
  TableSelectionPanelPluginProps
>(({ rowSelectionConfig, tableSelectionPanelRef }, ref) => {
  const stateRef = useRef<{
    selectedRowIds: number[];
    toggleSelection: (args: { rowIds: (string | number)[] }) => void;
  }>({
    selectedRowIds: [],
    toggleSelection: () => {},
  });

  useImperativeHandle(
    ref,
    () => {
      return {
        get selectedRowIds() {
          return stateRef.current.selectedRowIds;
        },
        get toggleSelection() {
          return stateRef.current.toggleSelection;
        },
      };
    },
    []
  );

  return (
    <Plugin name="TableSelectionPanelPlugin">
      <Template name="table">
        <TemplateConnector>
          {({ selection }: Getters, { toggleSelection }: Actions) => {
            const selectedRowIds = selection || [];

            // Обновляем актуальное состояние в ref
            stateRef.current = {
              selectedRowIds,
              toggleSelection,
            };

            const selectionPanel =
              selectedRowIds.length > 0 ? (
                <TableSelectionPanel
                  selectedRowIds={selectedRowIds}
                  rowSelectionConfig={rowSelectionConfig}
                  toggleSelection={toggleSelection}
                />
              ) : null;

            return (
              <>
                <TemplatePlaceholder />
                {selectionPanel &&
                  tableSelectionPanelRef?.current &&
                  createPortal(selectionPanel, tableSelectionPanelRef.current)}
              </>
            );
          }}
        </TemplateConnector>
      </Template>
    </Plugin>
  );
});

TableSelectionPanelPlugin.displayName = 'TableSelectionPanelPlugin';

export default memo(TableSelectionPanelPlugin, (prevProps, nextProps) => {
  return prevProps.rowSelectionConfig === nextProps.rowSelectionConfig;
});
